
import Vue from "vue";
import ProofreadingEditor from "@/components/molecules/ProofreadingEditor.vue";
import AssistFab from "@/components/atoms/AssistFab.vue";
import { debounce } from "lodash";

import { isMobile as isMobileCheck } from "@/constants/breakpoints";
import { getEffectiveEditorTheme } from "@/lib/utils/theme";

const Platform = require("platform");

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ProofreadingEditor,
    AssistFab,
  },
  props: {
    title: String,
    content: String,
  },
  data() {
    return {
      textareaHeight: 0,
      scrollTop: 0,
      scrollLeft: 0,
      resizeObserver: null,
      lineBreakPosition: false,
      compositionEndedFlag: false,
      isMobile: false,
      debouncedUpdateMobileStatus: null,
    };
  },
  mounted() {
    /** ResizeObserverを定義 */
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs.textarea as HTMLTextAreaElement);

    // デバウンシングされたモバイル判定関数を作成
    this.debouncedUpdateMobileStatus = debounce(this.updateMobileStatus, 150);

    // モバイル判定の初期化
    this.updateMobileStatus();
    window.addEventListener("resize", this.debouncedUpdateMobileStatus);

    // textareaHeightを初期化（スクロール計算に必要）
    this.$nextTick(() => {
      this.onResize();
    });
  },
  destroyed() {
    this.resizeObserver!.disconnect();
    window.removeEventListener("resize", this.debouncedUpdateMobileStatus);

    // デバウンシング関数のクリーンアップ
    if (this.debouncedUpdateMobileStatus) {
      this.debouncedUpdateMobileStatus.cancel();
    }
  },
  watch: {
    scrollRatio(newVal) {
      const textarea = this.$refs.textarea as HTMLTextAreaElement;
      if (!textarea || !(this.textareaHeight > 0) || !Number.isFinite(newVal)) return;
      const clamped = Math.min(1, Math.max(0, newVal));
      textarea.scroll(0, this.textareaHeight * clamped);
    },
    content() {
      this.$nextTick(() => this.onResize());
    },
  },
  computed: {
    theme() {
      const storeTheme = this.$store.getters["manuscriptModule/theme"];
      const effectiveAppTheme = this.$store.getters["generalModule/effectiveTheme"] as "light" | "dark";
      return getEffectiveEditorTheme(storeTheme, effectiveAppTheme);
    },
    scrollRatio() {
      return this.$store.getters["manuscriptModule/scrollRatio"];
    },
    isEnableAutoIndent() {
      return this.$store.getters["manuscriptModule/autoIndent"];
    },
    isFirstTime() {
      return this.$store.getters["manuscriptModule/isFirstTimeAutoIndent"];
    },
    isMultiEditorPath() {
      return window.location.pathname.includes("multiEditor");
    },
    isAssistPanelVisible() {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },
    isShowAssistFab() {
      return this.$store.getters["manuscriptModule/isShowAssistFab"];
    },
    isVerticalWritingMode() {
      return this.$store.getters["manuscriptModule/isVerticalWritingMode"];
    },
    isVerticalWritingModeActive() {
      // スマホ表示時は縦書きモード設定を無視
      return !this.isMobile && this.isVerticalWritingMode;
    },
    enabledSearchReplace() {
      return this.$store.getters["manuscriptModule/enabledSearchReplace"];
    },
    searchTargetIndex() {
      return this.$store.getters["manuscriptModule/searchTargetIndex"];
    },
    searchTotalCount() {
      return this.$store.getters["manuscriptModule/searchTotalCount"];
    },
  },
  methods: {
    onChangeTitle(e) {
      if (e.target instanceof HTMLInputElement) {
        const inputEvent = e as InputEvent;
        const emitArgs: IEditorEmitArgs = { value: e.target.value, isComposing: inputEvent.isComposing || false };
        this.$emit("changeTitle", emitArgs);
      }
    },
    onFixed(text) {
      this.$emit("changeContent", { value: text, isComposing: false });
    },
    onChangeContent(e) {
      if (e.target instanceof HTMLTextAreaElement) {
        const inputEvent = e as InputEvent;
        const emitArgs: IEditorEmitArgs = { value: e.target.value, isComposing: inputEvent.isComposing || false };
        this.$emit("changeContent", emitArgs);
      }
    },
    compositionStart() {
      this.$emit("compositionStart");
    },
    compositionEnd() {
      this.compositionEndedFlag = true;
      this.$emit("compositionEnd");
    },
    handleScroll(e, position) {
      this.scrollTop = position.scrollTop;
      this.scrollLeft = position.scrollLeft;

      // textareaHeightが0の場合はスクロール比率の更新をスキップ
      // (0で割るとInfinity/NaNになり、スクロール位置がリセットされる問題を防ぐ)
      if (this.textareaHeight <= 0) {
        this.onResize();
        return;
      }

      const scrollRatio = position.scrollTop / this.textareaHeight;
      this.$store.dispatch("manuscriptModule/updateScrollRatio", scrollRatio);
    },
    onResize() {
      const { textarea } = this.$refs as { textarea: HTMLTextAreaElement };
      this.textareaHeight = textarea.scrollHeight - textarea.clientHeight;
    },
    compositionUpdate(e) {
      if (!this.isEnableAutoIndent) {
        return;
      }
      const textarea = e.target as HTMLTextAreaElement;
      const caredPosition = textarea.selectionStart;
      // 字下げの対象がとなる文字候補
      const OPEN_BRACKETS = ["（", "＜", "「"];

      if (this.lineBreakPosition === caredPosition && e.data && OPEN_BRACKETS.includes(e.data.charAt(0))) {
        // 改行直後の開きカッコは字下げしないように、挿入した全角空白を削除する。
        document.execCommand("delete", false, undefined);

        if (Platform.name === "Chrome" || Platform.name === "Microsoft Edge") {
          // Chrome系ブラウザでは文末以外では、全角空白を削除した分カーソルの位置を移動しないと入力したい場所からずれる。
          // this.$nextTick を使うとカーソル位置が正常に移動しないため setTimeout を使用する。
          setTimeout(() => {
            const caretPosition = textarea.selectionStart;
            if (caretPosition !== textarea.value.length) {
              // eslint-disable-next-line no-multi-assign
              textarea.selectionStart = textarea.selectionEnd = caretPosition - 1;
            }
          }, 0);
        }
      }
    },
    keyDown(e) {
      if (!this.isEnableAutoIndent) {
        return;
      }

      const textarea = e.target as HTMLTextAreaElement;

      if (e.code === "Enter") {
        // Safari では keyDown の前に compositionEnd が呼ばれ
        // IME入力確定時の Enter イベントで isComposing が false になるための対策
        const skipLineBreak = Platform.name === "Safari" && this.compositionEndedFlag;

        if (!e.isComposing && !skipLineBreak) {
          e.preventDefault();
          const caredPosition = textarea.selectionStart;
          if (this.lineBreakPosition === caredPosition) {
            // 連続で改行が入力された時は、空白行から挿入した全角空白を削除する。
            document.execCommand("delete", false, undefined);
          }

          // eslint-disable-next-line no-irregular-whitespace
          document.execCommand("insertText", false, `\n　`);

          // 初めての字下げ処理が走ったタイミングでユーザーにメニューからの切り替え方法を通知する。
          if (this.isFirstTime) {
            this.$store.dispatch("manuscriptModule/showAutoIndentMessage");
          }

          this.$nextTick(() => {
            this.lineBreakPosition = textarea.selectionStart;

            // e.preventDefault() すると自動的にカーソル表示位置にスクロールしない問題に対応するためのハック
            // 参考: https://stackoverflow.com/questions/29899364
            textarea.blur();
            textarea.focus();
          });
          return;
        }
      }
      if (this.lineBreakPosition !== textarea.selectionStart) {
        this.$nextTick(() => {
          this.lineBreakPosition = false;
        });
      }

      this.compositionEndedFlag = false;
    },
    handleAssistClick() {
      // アシスト機能の呼び出し
      console.log("AssistFab clicked in Editor");

      // 現在の状態をログ出力
      console.log("Current assist panel state:", {
        isVisible: this.isAssistPanelVisible,
      });

      // アシストパネルの表示/非表示を切り替え
      if (this.isAssistPanelVisible) {
        this.$store.dispatch("assistNavigationModule/hidePanel");
      } else {
        this.$store.dispatch("assistNavigationModule/showPanel");
      }

      // 更新後の状態をログ出力
      this.$nextTick(() => {
        console.log("Updated assist panel state:", {
          isVisible: this.isAssistPanelVisible,
        });
      });
    },
    updateMobileStatus() {
      this.isMobile = isMobileCheck(); // SCSSの@include mq(sp)と一致
    },
    goToNextSearchResult() {
      const { searchTargetIndex, searchTotalCount } = this;
      if (searchTotalCount === 0) return;

      const nextTargetIndex = searchTargetIndex === searchTotalCount - 1 ? 0 : searchTargetIndex + 1;
      this.$store.dispatch("manuscriptModule/updateSearchTargetIndex", nextTargetIndex);
    },
  },
});

interface Props {
  title: string;
  content: string;
}

interface Data {
  textareaHeight: number;
  resizeObserver: ResizeObserver | null;
  scrollTop: number;
  scrollLeft: number;
  lineBreakPosition: boolean | number;
  compositionEndedFlag: boolean;
  isMobile: boolean;
  debouncedUpdateMobileStatus: ReturnType<typeof debounce> | null;
}

interface Computed {
  theme: string;
  scrollRatio: number;
  isEnableAutoIndent: boolean;
  isFirstTime: boolean;
  isMultiEditorPath: boolean;
  isAssistPanelVisible: boolean;
  isShowAssistFab: boolean;
  isVerticalWritingMode: boolean;
  isVerticalWritingModeActive: boolean;
  enabledSearchReplace: boolean;
  searchTargetIndex: number;
  searchTotalCount: number;
}

interface Methods {
  onChangeTitle(e: Event): void;
  onChangeContent(e: Event): void;
  onFixed(value: string): void;
  compositionStart(): void;
  compositionEnd(): void;
  handleScroll(e: Event, position: any): void;
  onResize(): void;
  compositionUpdate(e: CompositionEvent): void;
  keyDown(e: KeyboardEvent): void;
  handleAssistClick(): void;
  updateMobileStatus(): void;
  goToNextSearchResult(): void;
}

export interface IEditorEmitArgs {
  value: string;
  isComposing: boolean;
}
